/**
 * Welcome onboarding screen
 *
 * Route: `/webAppOnboarding` (index). Shows invitation preview, course summary, and entry CTA.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` — requires URL `signUpId`, Zustand persist hydration, and
 *   resolved invite context (`getSignedUpPatientInviteContext`) before rendering; errors if id missing.
 * - Auth: none — outside `OnboardingAuthGuard` (`ONBOARDING_SCREEN_META.requiresFirebaseSession: false`).
 * - Onboarding state: none — outside `OnboardingCompletedGuard` (completed patients are not redirected here).
 * - Screen-local: `useInviteContext()`; shows loading until invite context (and optional course content) is ready.
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { space } from '../../../styles/onboardingUiTokens';
import { TrainingSummary } from '../components/content/TrainingSummary';
import { LoadingSpinner } from '../components/elements/LoadingSpinner';
import { Notice } from '../components/elements/Notice';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1, H3 } from '../components/typography/HeadingLevels';
import { List, ListItem } from '../components/typography/List';
import { checkMarker } from '../components/typography/listMarkers';
import { useCourseContent } from '../hooks/useCourseContent';
import { useInviteContext } from '../hooks/useInviteContext';
import { useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: false,
} as const;

const DynamicLoadingArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  width: 100%;
`;

/** Welcome intro copy (ERS-61 C1.2); names therapist and practice where the invite context provides them. */
function resolveWelcomeIntro(therapistFirstName: string | undefined, practiceName: string | undefined): string {
  const trimmedTherapist = therapistFirstName?.trim() ?? '';
  const trimmedPractice = practiceName?.trim() ?? '';
  const recommendation = 'hat dir folgenden Gesundheitskurs empfohlen.';

  if (trimmedTherapist && trimmedPractice) {
    return `${trimmedTherapist} von ${trimmedPractice} ${recommendation}`;
  }
  if (trimmedTherapist) {
    return `${trimmedTherapist} ${recommendation}`;
  }
  return trimmedPractice ? `Deine Praxis ${trimmedPractice} ${recommendation}` : `Deine Praxis ${recommendation}`;
}

export function WebOnboardingWelcomeScreen() {
  useInviteContext();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const inviteContext = useOnboardingStore((s) => s.inviteContext);
  const inviteContextStatus = useOnboardingStore((s) => s.inviteContextStatus);
  const contentCourseId = inviteContext?.courseId?.trim() ?? '';
  const courseContent = useCourseContent(contentCourseId, { preloadImages: true });

  const goToVerify = () => {
    posthog.capture(PATIENT_TRACKING_EVENTS.web_welcome_completed);
    navigate('phone-verification', { relative: 'path' });
  };

  const isInviteLoading = inviteContextStatus === 'idle' || inviteContextStatus === 'loading';
  const isCourseLoading = Boolean(contentCourseId) && courseContent.status === 'loading';

  const dynamicContent = (() => {
    if (isInviteLoading || isCourseLoading) {
      return (
        <DynamicLoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </DynamicLoadingArea>
      );
    }

    if (!contentCourseId) {
      return (
        <Notice preset="warning" title="Kurs nicht verfügbar">
          Für diese Einladung ist kein Kurs hinterlegt.
        </Notice>
      );
    }

    if (courseContent.status === 'error') {
      return (
        <Notice preset="warning" title="Kursdaten">
          {courseContent.message}
        </Notice>
      );
    }

    if (courseContent.status !== 'ready') {
      return null;
    }

    return (
      <>
        <Body tone="muted" variant="document">
          {resolveWelcomeIntro(inviteContext?.therapistFirstName, inviteContext?.practiceName)}
        </Body>
        <TrainingSummary
          title={null}
          boxed={false}
          showProgress={false}
          weeksInCourse={0}
          heroImageSrc={courseContent.heroImageSrc}
          thumbnailSrcs={courseContent.thumbnailSrcs}
          heroImageSrcs={courseContent.heroImageSrcs}
        />
        <H3>Das erwartet dich in deinem Kurs:</H3>
        {courseContent.previewBulletPoints.length > 0 ? (
          <List {...checkMarker} iconBoxed listPadding="0">
            {courseContent.previewBulletPoints.map((point, index) => (
              <ListItem key={`${index}-${point}`}>
                <Body tone="muted" variant="document">
                  {point}
                </Body>
              </ListItem>
            ))}
          </List>
        ) : null}
      </>
    );
  })();

  return (
    <OnboardingLayout currentStep={1} totalSteps={6} forwardLabel="Jetzt starten" onForward={goToVerify}>
      <OnboardingHeader hideBackButton />
      <OnboardingBody>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: space.md,
            paddingBottom: space.xl,
          }}
        >
          <H1>Herzlich willkommen zu deinem Physiofit Kurs.</H1>
          {dynamicContent}
        </div>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
